import { useEffect, useRef, useState } from "react";
import {
  usesSanctionsOracle,
  SANCTIONS_COVERAGE_MESSAGE,
} from "../../utils/wallet-screening";

export type ScreeningStatus =
  | "idle"
  | "checking"
  | "passed"
  | "blocked"
  | "error";

export type ComplianceToastState = {
  type: "success" | "error" | "info";
  message: string;
} | null;

interface UseComplianceToastParams {
  enabled: boolean;
  connected: boolean;
  screeningStatus: ScreeningStatus;
  screeningError: string | null;
  autoHideMs?: number;
}

export function useComplianceToast({
  enabled,
  connected,
  screeningStatus,
  screeningError,
  autoHideMs = 5000,
}: UseComplianceToastParams): ComplianceToastState {
  const [toast, setToast] = useState<ComplianceToastState>(null);
  const lastStatusRef = useRef<ScreeningStatus | null>(null);

  useEffect(() => {
    if (!enabled || !connected) {
      setToast(null);
      lastStatusRef.current = null;
      return;
    }

    if (screeningStatus === "idle" || screeningStatus === "checking") {
      return;
    }

    if (lastStatusRef.current === screeningStatus) {
      return;
    }
    lastStatusRef.current = screeningStatus;

    if (screeningStatus === "passed") {
      setToast({
        type: "success",
        message: usesSanctionsOracle()
          ? SANCTIONS_COVERAGE_MESSAGE
          : "Wallet screening passed.",
      });
      return;
    }

    if (screeningStatus === "blocked") {
      setToast({
        type: "error",
        message: screeningError
          ? `Compliance blocked: ${screeningError}`
          : "Compliance blocked. Wallet failed screening.",
      });
      return;
    }

    if (screeningStatus === "error") {
      setToast({
        type: "error",
        message: "Compliance check failed. Please reconnect and try again.",
      });
    }
  }, [enabled, connected, screeningStatus, screeningError]);

  useEffect(() => {
    if (!toast) return;
    const timeout = window.setTimeout(() => {
      setToast(null);
    }, autoHideMs);
    return () => window.clearTimeout(timeout);
  }, [toast, autoHideMs]);

  return toast;
}
